import React, { useEffect } from 'react';
import { Button, Typography, Row, Col, Card, Space, Divider, Tag } from 'antd';
import { 
  LoginOutlined, 
  UserAddOutlined, 
  ShoppingOutlined, 
  TeamOutlined, 
  CheckCircleOutlined,
  RocketOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  MailOutlined,
  BarChartOutlined,
  ShopOutlined,
  SmileOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import './HomePage.css';

const { Title, Paragraph, Text, Link } = Typography;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const appOrigin = (() => {
    const host = window.location.hostname;
    if (host.startsWith('app.')) return window.location.origin;
    if (host === 'localhost' || host === '127.0.0.1') return window.location.origin;
    return `${window.location.protocol}//app.${host}`;
  })();


  const goToApp = (path: string) => {
    const host = window.location.hostname;
    // If we're already on app.* just navigate within SPA
    if (host.startsWith('app.') || host === 'localhost' || host === '127.0.0.1') {
      navigate(path);
      return;
    }
    window.location.href = `${appOrigin}${path}`;
  };

  const goToGuide = () => {
    navigate('/platform-guide');
  };

  // Авторизованных пользователей перенаправляем в личный кабинет
  useEffect(() => {
    if (user) {
      navigate('/app', { replace: true });
    }
  }, [user, navigate]);

  const features = [
    {
      icon: <ShoppingOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: 'Удобное меню',
      description: 'Широкий выбор блюд от проверенных поставщиков с подробным описанием и фотографиями'
    },
    {
      icon: <ClockCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      title: 'Быстрая доставка',
      description: 'Заказы доставляются точно в срок, с возможностью отслеживания статуса'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '48px', color: '#faad14' }} />,
      title: 'Для всей команды',
      description: 'Управление заказами для всего коллектива с индивидуальными лимитами и предпочтениями'
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '48px', color: '#f5222d' }} />,
      title: 'Безопасность',
      description: 'Защита персональных данных и безопасные платежи'
    }
  ];

  const audiences = [
    {
      icon: <SmileOutlined style={{ fontSize: 22, color: '#1677ff' }} />,
      title: 'Сотрудникам',
      description: 'Заказ за пару минут, напоминания и история заказов'
    },
    {
      icon: <TeamOutlined style={{ fontSize: 22, color: '#52c41a' }} />,
      title: 'Компаниям',
      description: 'Лимиты, правила заказа, прозрачность и отчётность'
    },
    {
      icon: <ShopOutlined style={{ fontSize: 22, color: '#faad14' }} />,
      title: 'Поставщикам',
      description: 'Каталог, планирование меню, комбо-наборы, чек-листы'
    },
    {
      icon: <BarChartOutlined style={{ fontSize: 22, color: '#f5222d' }} />,
      title: 'Руководству',
      description: 'Аналитика, контроль расходов и процесс без хаоса'
    }
  ];

  const benefits = [
    'Экономия времени на организации питания сотрудников',
    'Прозрачное ценообразование без скрытых комиссий',
    'Гибкая система лимитов и контроля расходов',
    'Удобная аналитика и отчетность',
    'Поддержка 24/7'
  ];

  return (
    <div className="homepage">
      {/* Header */}
      <header className="homepage-header">
        <div className="container">
          <Row
            gutter={[12, 12]}
            align="middle"
            style={{ padding: '16px 0' }}
          >
            <Col xs={24} sm={10} md={8}>
              <div className="brand">
                <svg
                  className="brand-mark"
                  viewBox="0 0 64 64"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="luemenMark" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.55" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M16 14C16 12.895 16.895 12 18 12h9c1.105 0 2 .895 2 2v24.5c0 4.97 4.03 9 9 9H48c1.105 0 2 .895 2 2v8c0 1.105-.895 2-2 2H38c-12.15 0-22-9.85-22-22V14z"
                    fill="url(#luemenMark)"
                  />
                  <path
                    d="M46 13a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H36a2 2 0 0 1-2-2V15a2 2 0 0 1 2-2h10z"
                    fill="rgba(255,255,255,0.25)"
                  />
                </svg>
                <Title level={2} className="brand-name">
                  Luemen
                </Title>
              </div>
            </Col>
            <Col xs={24} sm={14} md={16} className="header-actions">
              <Space size="middle" wrap>
                <Space size="small" className="docs-links">
                  <Button
                    type="text"
                    onClick={() => navigate('/terms')}
                    style={{ color: '#fff' }}
                  >
                    Пользовательское соглашение
                  </Button>
                  <Button
                    type="text"
                    onClick={() => navigate('/privacy')}
                    style={{ color: '#fff' }}
                  >
                    Политика конфиденциальности
                  </Button>
                </Space>
                <Button icon={<LoginOutlined />} onClick={() => goToApp('/login')}>
                  Войти
                </Button>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => goToApp('/register')}
                >
                  Регистрация
                </Button>
                <Button onClick={goToGuide} icon={<ArrowRightOutlined />}>
                  Гайд по платформе
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <Row justify="center" align="middle" className="hero-row">
            <Col xs={24} lg={18}>
              <div className="hero-content">
                <Tag className="hero-badge">Платформа для корпоративных обедов</Tag>
                <Title level={1} className="hero-title">
                  Организация питания сотрудников —
                  <br />
                  просто, прозрачно, удобно
                </Title>
                <Paragraph className="hero-subtitle">
                  Luemen связывает компании и поставщиков питания: меню, комбо‑наборы, дедлайны заказов,
                  чек‑листы сборки и отчётность — в одном месте.
                </Paragraph>
                <div className="hero-cta">
                  <Button
                    type="primary"
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={() => goToApp('/register')}
                    className="hero-primary"
                  >
                    Начать работу
                  </Button>
                  <Button
                    size="large"
                    icon={<LoginOutlined />}
                    onClick={() => goToApp('/login')}
                    className="hero-secondary"
                  >
                    Войти
                  </Button>
                  <Button
                    size="large"
                    onClick={goToGuide}
                    className="hero-tertiary"
                    icon={<ArrowRightOutlined />}
                  >
                    Гайд по платформе
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Audience */}
      <section className="audience-section">
        <div className="container">
          <Row justify="center" style={{ marginBottom: 36 }}>
            <Col xs={24} lg={18} style={{ textAlign: 'center' }}>
              <Title level={2} style={{ marginBottom: 12 }}>Кому подходит</Title>
              <Paragraph style={{ fontSize: 18, color: '#667085', marginBottom: 0 }}>
                Инструменты для всех ролей: от сотрудника до поставщика и админа.
              </Paragraph>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            {audiences.map((a, idx) => (
              <Col xs={24} sm={12} lg={6} key={idx}>
                <Card className="soft-card" bordered={false}>
                  <div className="soft-card-icon">{a.icon}</div>
                  <Title level={5} style={{ marginBottom: 8 }}>{a.title}</Title>
                  <Paragraph style={{ marginBottom: 0, color: '#667085' }}>{a.description}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" style={{ padding: '80px 0', background: '#fff' }}>
        <div className="container">
          <Row justify="center" style={{ marginBottom: '60px' }}>
            <Col xs={24} lg={18}>
              <Title level={2} style={{ textAlign: 'center', marginBottom: '20px' }}>
                Почему выбирают Luemen?
              </Title>
              <Paragraph style={{ textAlign: 'center', fontSize: '18px', color: '#666' }}>
                Современная платформа с удобным интерфейсом и надежной поддержкой
              </Paragraph>
            </Col>
          </Row>
          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card hoverable className="feature-card">
                  <div style={{ marginBottom: '20px' }}>
                    {feature.icon}
                  </div>
                  <Title level={4}>{feature.title}</Title>
                  <Paragraph style={{ color: '#666' }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section" style={{ padding: '80px 0', background: '#f5f5f5' }}>
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Title level={2} style={{ marginBottom: '30px' }}>
                Преимущества для вашей компании
              </Title>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {benefits.map((benefit, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '16px', marginTop: '4px' }} />
                    <Text style={{ fontSize: '16px' }}>{benefit}</Text>
                  </div>
                ))}
              </Space>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="soft-card" bordered={false}>
                <Title level={3} style={{ marginBottom: '20px' }}>
                  Как это работает?
                </Title>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>1. Регистрация</Text>
                    <Paragraph style={{ marginBottom: 0 }}>Зарегистрируйтесь как заказчик или поставщик</Paragraph>
                  </div>
                  <Divider />
                  <div>
                    <Text strong>2. Подключение</Text>
                    <Paragraph style={{ marginBottom: 0 }}>Найдите подходящих партнёров и установите связь</Paragraph>
                  </div>
                  <Divider />
                  <div>
                    <Text strong>3. Работа</Text>
                    <Paragraph style={{ marginBottom: 0 }}>Начните принимать и обрабатывать заказы</Paragraph>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="cta-section"
        style={{ padding: '80px 0', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
      >
        <div className="container">
          <Row justify="center">
            <Col xs={24} lg={16} style={{ textAlign: 'center' }}>
              <Title level={2} style={{ color: '#fff', marginBottom: '20px' }}>
                Готовы начать?
              </Title>
              <Paragraph style={{ color: '#fff', fontSize: '18px', marginBottom: '40px' }}>
                Присоединяйтесь к платформе Luemen и упростите организацию питания в вашей компании
              </Paragraph>
              <div className="cta-actions">
                <Button
                  type="primary"
                  size="large"
                  icon={<UserAddOutlined />}
                  onClick={() => goToApp('/register')}
                  className="cta-primary"
                >
                  Зарегистрироваться
                </Button>
                <Button
                  size="large"
                  icon={<LoginOutlined />}
                  onClick={() => goToApp('/login')}
                  className="cta-secondary"
                >
                  Войти
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer" style={{ padding: '40px 0', background: '#001529', color: '#fff' }}>
        <div className="container">
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} lg={8}>
              <Title level={4} style={{ color: '#fff', marginBottom: '20px' }}>
                Luemen
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.65)' }}>
                Платформа для организации питания сотрудников
              </Paragraph>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Title level={5} style={{ color: '#fff', marginBottom: '20px' }}>
                Документы
              </Title>
              <Space direction="vertical" size="small">
                <Link 
                  onClick={() => navigate('/terms')} 
                  style={{ color: 'rgba(255,255,255,0.65)' }}
                >
                  Пользовательское соглашение
                </Link>
                <Link 
                  onClick={() => navigate('/privacy')} 
                  style={{ color: 'rgba(255,255,255,0.65)' }}
                >
                  Политика конфиденциальности
                </Link>
              </Space>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Title level={5} style={{ color: '#fff', marginBottom: '20px' }}>
                Контакты
              </Title>
              <Space direction="vertical" size="small">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <MailOutlined style={{ marginRight: '8px' }} />
                  <Link href="mailto:impulseplat@gmail.com" style={{ color: 'rgba(255,255,255,0.65)' }}>
                    impulseplat@gmail.com
                  </Link>
                </div>
                <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
                  По всем вопросам пишите на электронную почту
                </Text>
              </Space>
            </Col>
          </Row>
          <Divider style={{ borderColor: 'rgba(255,255,255,0.2)' }} />
          <Row justify="center">
            <Col>
              <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
                © {new Date().getFullYear()} Luemen. Все права защищены.
              </Text>
            </Col>
          </Row>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;

